import {
  Button,
  CellFormatOptions,
  ColumnTable,
  Icon,
  TableCollapsable,
  addToast,
} from '@octano/global-ui';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { Card, Col, Row } from 'reactstrap';

import {
  getStudentPostCloseDetail,
  updateStudentPostCloseGrades,
} from '../../api/requests/studentsPostClose';
import { PathsLayouts } from '../../config/routes';
import { StudentPostCloseDetail } from '../../types/studentPostClose';
import { gradeToFixed } from '../../utils/math';
import {
  SearchParams,
  StudentStatusBadge,
} from '../StudentsPostCloseSearch/parts';
import { mapStudentPostCloseDetailResponse } from './helpers';
import {
  EditableGrade,
  StudentAndSectionInfoBox,
  useConfigurableGradesValidator,
} from './parts';
import SubevaluationsTable from './parts/SubevaluationsTable';

type MappedData = ReturnType<typeof mapStudentPostCloseDetailResponse>;
type DetailBoxData = MappedData['summary'];
type TableData = MappedData['grades'];

const prefix = 'studentGradesClosure.detail';

export function StudentsPostCloseDetail() {
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(true);
  const [tableData, setTableData] = useState<TableData>([]);
  const [detailBoxData, setDetailBoxData] = useState<DetailBoxData>();

  const [isUpdatingGrades, setIsUpdatingGrades] = useState(false);

  const {
    formatGrade,
    isRequestingConfig,
    errorGettingConfig,
    config,
  } = useConfigurableGradesValidator();

  const [pendingTestGradesChanges, setPendingTestGradesChanges] = useState<
    { testGradeId: number; grade: string | number }[]
  >([]);
  const [pendingExamGradeChange, setPendingExamGradeChange] = useState<{
    finalGradeId: number;
    grade: string | number;
  }>();

  const {
    state: { courseEnrollmentId, savedSearchParams } = {
      courseEnrollmentId: null,
      savedSearchParams: undefined,
    },
  } = useLocation<{
    courseEnrollmentId: string | number;
    savedSearchParams: SearchParams;
  }>();
  const history = useHistory();

  if (!courseEnrollmentId) {
    history.push(PathsLayouts.studentsPostClose);
  }

  const goBack = () => {
    history.push(PathsLayouts.studentsPostClose, { savedSearchParams });
  };

  const isColoredRow = (row: StudentPostCloseDetail.Grade) => {
    console.log(row);
    return ['partials-total', 'final'].includes(row.gradeType);
  };
  const isBoldRow = (row: StudentPostCloseDetail.Grade) =>
    ['partials-total', 'exam-grade', 'final'].includes(row.gradeType);

  const getData = useCallback(async () => {
    setIsLoading(true);

    const { data, error } = await getStudentPostCloseDetail(
      courseEnrollmentId!,
    );

    if (error) {
      console.log(error);
    } else if (data) {
      const { grades, summary } = mapStudentPostCloseDetailResponse(data);
      setDetailBoxData(summary);
      setTableData(grades);
    }

    setIsLoading(false);
  }, [courseEnrollmentId]);

  const handleSave = async () => {
    setIsUpdatingGrades(true);

    const studentTestGrades = pendingTestGradesChanges.map((tg) => ({
      testGradeId: tg.testGradeId,
      grade: gradeToFixed(String(tg.grade), config.decimalsLength),
    }));

    const studentFinalGrade = pendingExamGradeChange && {
      finalGradeId: pendingExamGradeChange.finalGradeId,
      grade: gradeToFixed(
        String(pendingExamGradeChange.grade),
        config.decimalsLength,
      ),
    };

    const { error } = await updateStudentPostCloseGrades({
      courseEnrollmentId: `${courseEnrollmentId}`,
      studentTestGrades,
      studentFinalGrade,
    });

    if (error) {
      addToast({
        icon: 'error',
        color: 'danger',
        text: t(`${prefix}.errorWhileUpdating`),
      });
    } else {
      // Reseteamos los cambios pendientes
      setPendingTestGradesChanges([]);
      setPendingExamGradeChange(undefined);

      await getData();
      addToast({
        icon: 'success',
        color: 'success',
        text: t(`${prefix}.updatedSuccessfully`),
      });
    }

    setIsUpdatingGrades(false);
  };

  /*
    Solo se usa para exam grade y test grades, no todas las notas de la tabla, algunas
    son calculadas y no editables
  */
  const handleGradeEdit = (
    grade:
      | StudentPostCloseDetail.Grade
      | StudentPostCloseDetail.PartialEvaluationSubevaluationsGrade,
    newValue: string,
  ) => {
    if (StudentPostCloseDetail.hasTestGradeId(grade)) {
      // Conseguimos la nota original para saber si el valor ingresado es distinto al ya guardado
      const originalGrade = tableData.find((g) => {
        return (
          StudentPostCloseDetail.hasTestGradeId(g) &&
          g.testGradeId === grade.testGradeId
        );
      });

      setPendingTestGradesChanges((curr) => {
        const filteredChanges = curr.filter(
          (gc) => gc.testGradeId !== grade.testGradeId,
        );

        // Si no es igual al anterior, lo añadimos como cambio pendiente por guardar
        return originalGrade?.grade !== +newValue
          ? [...filteredChanges, { ...grade, grade: +newValue }]
          : filteredChanges;
      });
    } else if (StudentPostCloseDetail.isExamGrade(grade)) {
      // Conseguimos la nota original para saber si el valor ingresado es distinto al ya guardado
      const originalGrade = tableData.find((g) => {
        return (
          StudentPostCloseDetail.isExamGrade(g) &&
          g.finalGradeId === grade.finalGradeId
        );
      });

      // Si no es igual al anterior, lo añadimos como cambio pendiente por guardar
      if (originalGrade?.grade !== +newValue) {
        setPendingExamGradeChange({ ...grade, grade: +newValue });
      } else {
        setPendingExamGradeChange(undefined);
      }
    }
  };

  const COLUMNS: ColumnTable<TableData[number]>[] = [
    {
      headerText: t(`${prefix}.test`),
      columnName: 'name',
      thClassName: 'text-center',
      tdClassName: ({ index, row }) =>
        clsx('text-center', {
          'bg-tertiary': isColoredRow(row),
          'fw-600': isBoldRow(row),
        }),
      cellFormat: ({ row }) => {
        if (
          StudentPostCloseDetail.isTestGrade(row) &&
          row.type === 'subevaluations'
        ) {
          return (
            <>
              {row.name}
              <span className="mx-1">
                <Icon name="stack" color="primary" />
              </span>
              {row.grades.length}
            </>
          );
        }
        return <>{row.name}</>;
      },
    },
    {
      headerText: t(`${prefix}.grade`),
      columnName: 'grade',
      thClassName: 'text-center',
      tdClassName: ({ row }) =>
        clsx('d-flex justify-content-center', {
          'bg-tertiary': isColoredRow(row),
          'fw-600': isBoldRow(row),
        }),
      cellFormat: (options) => {
        const userHasMadeChanges =
          pendingExamGradeChange || pendingTestGradesChanges.length;

        if (isColoredRow(options.row) && userHasMadeChanges) {
          return (
            <p className="mb-0 d-flex align-items-center">
              {t(`${prefix}.calculating`)}
            </p>
          );
        }

        return (
          <EditableGrade
            initialGrade={options.value}
            onEdit={(newValue) => handleGradeEdit(options.row, newValue)}
            editable={
              !isRequestingConfig &&
              !errorGettingConfig &&
              !isColoredRow(options.row) &&
              !(
                StudentPostCloseDetail.isTestGrade(options.row) &&
                options.row.type === 'subevaluations'
              )
            }
            inputFormatter={formatGrade}
          />
        );
      },
    },
    {
      headerText: t(`${prefix}.percentage`),
      columnName: 'percentage',
      thClassName: 'text-center',
      tdClassName: ({ row }) =>
        clsx('text-center', {
          'bg-tertiary': isColoredRow(row),
          'fw-600': isBoldRow(row),
        }),
    },
    {
      headerText: t(`${prefix}.date`),
      columnName: 'date',
      thClassName: 'text-center',
      cellFormat: ({ value }) =>
        value ? dayjs(value).format('DD/MM/YYYY') : '-',
      tdClassName: ({ row }) =>
        clsx('text-center', {
          'bg-tertiary': isColoredRow(row),
          'fw-600': isBoldRow(row),
        }),
    },
  ];

  useEffect(() => {
    getData();
  }, [getData]);

  return (
    <div className="student-grades-closure mx-3">
      <Card className="p-4">
        <div
          className="d-flex flex-wrap justify-content-between mb-4"
          style={{ gap: 12 }}
        >
          <Button
            icon="back"
            text={t(`${prefix}.goBack`)}
            size="sm"
            rounded
            outlined
            onClick={goBack}
            style={{ height: 'fit-content' }}
          />
          {detailBoxData && <StudentAndSectionInfoBox {...detailBoxData} />}
        </div>
        <h2 className="text-primary text-uppercase fs-18 fw-600 mt-2">
          {t(`${prefix}.title`)}
        </h2>

        <Row>
          <Col className="pb-4" md={10}>
            <p className="text-medium text-justify fs-14 mb-0">
              {t(`${prefix}.description`)}
            </p>
          </Col>
          <Col
            className="pb-4 d-flex align-content-center "
            style={{ gap: 8, minWidth: 212 }}
            md={2}
          >
            <p className="pt-1">{t(`${prefix}.currentStatus`)}: </p>
            <StudentStatusBadge
              status={
                pendingExamGradeChange ||
                pendingTestGradesChanges.length ||
                !detailBoxData ||
                isLoading
                  ? 'calc'
                  : detailBoxData.state
              }
            />
          </Col>
        </Row>

        <TableCollapsable<StudentPostCloseDetail.Grade>
          isLoading={isLoading}
          data={tableData}
          collapsableTdProps={({ row }) => ({
            className: clsx({ 'bg-tertiary': isColoredRow(row) }),
          })}
          isCollapsable={(row) => {
            return (
              StudentPostCloseDetail.isTestGrade(row) &&
              row.type === 'subevaluations'
            );
          }}
          columns={COLUMNS}
          secondColumn={{
            columnName: 'second',
            headerText: 'second',
            cellFormat: ({
              row,
            }: CellFormatOptions<StudentPostCloseDetail.PartialEvaluationSubevaluations>) => (
              <SubevaluationsTable
                handleGradeEdit={handleGradeEdit}
                partialEvaluation={row}
                formatGrade={formatGrade}
                isRequestingConfig={isRequestingConfig}
                errorGettingConfig={errorGettingConfig}
              />
            ),
          }}
        />

        <div className="d-flex justify-content-end mt-5">
          <div
            className="d-flex flex-wrap justify-content-end "
            style={{ maxWidth: 1000, gap: 14 }}
          >
            <Button
              text={t(`${prefix}.cancel`)}
              outlined
              style={{ width: 333 }}
              onClick={goBack}
              className={'flex-grow-1'}
            />
            <Button
              text={t(`${prefix}.saveChanges`)}
              style={{ width: 333 }}
              className={'flex-grow-1'}
              onClick={handleSave}
              loading={isUpdatingGrades}
              disabled={
                !pendingExamGradeChange && !pendingTestGradesChanges.length
              }
            />
          </div>
        </div>
      </Card>
    </div>
  );
}
